import { TableRowValueList } from '@vinnova/ui';
import { Worksite } from '../../../../core/models/question/question-worksite.model';
import { Finansieringstyp, Kostnadstyp } from '../budget.interface';
import {
  BUDGET_IMPORT_ROW_SOKT_BIDRAG,
  BUDGET_IMPORT_UNIT_FACTOR,
  BudgetImportApplyProjektpart,
  BudgetImportCell,
  BudgetImportProjektpart,
  BudgetImportResult,
  BudgetImportUnit,
  BudgetSlotSchema,
} from './budget-import.interface';
import { BUDGET_IMPORT_TEXT, KOSTNADSSLAG_LABELS } from './budget-import.text';

export interface BudgetImportPreviewCell {
  ar: number;
  current: number;
  /** Scaled to SEK; undefined when the import had no value for this slot. */
  imported?: number;
  source?: string;
}

export interface BudgetImportPreviewRow {
  id: string;
  rowType?: number;
  finansiarId?: string;
  label: string;
  cells: BudgetImportPreviewCell[];
}

export interface BudgetImportPreviewPart {
  part: BudgetImportProjektpart;
  kostnader: BudgetImportPreviewRow[];
  finansiering: BudgetImportPreviewRow[];
  hasImportedValues: boolean;
}

/** Current table values per worksite id, in the same shape the budget component keeps in its form controls. */
export interface CurrentBudgetTables {
  kostnader: TableRowValueList;
  finansiering: TableRowValueList;
}

export class BudgetImportHelper {
  static scale(summa: number, unit: BudgetImportUnit): number {
    return Math.round(summa * (BUDGET_IMPORT_UNIT_FACTOR[unit] ?? 1));
  }

  static buildSchema(
    startDate: Date,
    endDate: Date,
    koordinator: Worksite,
    projektparter: Worksite[],
  ): BudgetSlotSchema {
    const toPart = (
      worksite: Worksite,
      koordinerande: boolean,
    ): BudgetImportProjektpart => ({
      id: worksite.id,
      koordinerande,
      namn: worksite.company?.name ?? worksite.name,
      arbetsstalleNamn: worksite.name,
      organisationsnummer: worksite.company?.nationalRegistrationNumber,
      adress: BudgetImportHelper.buildAdress(worksite),
      finansiarer: (worksite.financiers ?? []).map((f) => ({
        id: f.id,
        namn: f.company?.name ?? f.name,
      })),
    });

    return {
      startYear: startDate.getFullYear(),
      endYear: endDate.getFullYear(),
      projektparter: [
        toPart(koordinator, true),
        ...projektparter.map((p) => toPart(p, false)),
      ],
    };
  }

  /** Street, zip and city combined so a workbook caption naming only the city can still match. */
  private static buildAdress(worksite: Worksite): string | undefined {
    const street = worksite.address ?? worksite.company?.address;
    const zipcode = worksite.zipcode ?? worksite.company?.zipcode;
    const city = worksite.city ?? worksite.company?.city;
    return (
      [street, [zipcode, city].filter(Boolean).join(' ')]
        .filter(Boolean)
        .join(', ') || undefined
    );
  }

  static years(
    result: Pick<BudgetImportResult, 'startYear' | 'endYear'>,
  ): number[] {
    const years: number[] = [];
    for (let year = result.startYear; year <= result.endYear; year++) {
      years.push(year);
    }
    return years;
  }

  static buildPreview(
    result: BudgetImportResult,
    unit: BudgetImportUnit,
    current: Map<string, CurrentBudgetTables>,
  ): BudgetImportPreviewPart[] {
    const years = BudgetImportHelper.years(result);
    const cellsByPart = new Map<string, BudgetImportCell[]>();
    for (const cell of result.cells) {
      const list = cellsByPart.get(cell.projektpartId) ?? [];
      list.push(cell);
      cellsByPart.set(cell.projektpartId, list);
    }

    return result.projektparter.map((part) => {
      const cells = cellsByPart.get(part.id) ?? [];
      const tables = current.get(part.id);

      const kostnader = Object.values(Kostnadstyp).map((kostnadstyp) =>
        BudgetImportHelper.buildRow(
          { rowType: Number(kostnadstyp) },
          kostnadstyp,
          KOSTNADSSLAG_LABELS[kostnadstyp],
          years,
          cells,
          tables?.kostnader ?? [],
          result.startYear,
          unit,
        ),
      );
      const finansiering = [
        BudgetImportHelper.buildRow(
          { rowType: BUDGET_IMPORT_ROW_SOKT_BIDRAG },
          Finansieringstyp.VINNOVABIDRAG,
          BUDGET_IMPORT_TEXT.soktBidrag,
          years,
          cells,
          tables?.finansiering ?? [],
          result.startYear,
          unit,
        ),
        ...(part.finansiarer ?? []).map((finansiar) =>
          BudgetImportHelper.buildRow(
            { finansiarId: finansiar.id },
            finansiar.id,
            finansiar.namn ?? '',
            years,
            cells,
            tables?.finansiering ?? [],
            result.startYear,
            unit,
          ),
        ),
      ];

      return {
        part,
        kostnader,
        finansiering,
        hasImportedValues: [...kostnader, ...finansiering].some((row) =>
          row.cells.some((cell) => cell.imported !== undefined),
        ),
      };
    });
  }

  /** Turns confirmed preview parts into per-part patches, dropping cells the import left empty. */
  static toApplyParts(
    preview: BudgetImportPreviewPart[],
  ): BudgetImportApplyProjektpart[] {
    return preview.map((p) => {
      const kostnader: Record<string, Record<number, number>> = {};
      for (const row of p.kostnader) {
        for (const cell of row.cells) {
          if (cell.imported === undefined) {
            continue;
          }
          const rowId = row.rowType?.toString() ?? row.id;
          kostnader[rowId] ??= {};
          kostnader[rowId][cell.ar] = cell.imported;
        }
      }
      const bidrag: Record<number, number> = {};
      for (const cell of p.finansiering[0]?.cells ?? []) {
        if (cell.imported !== undefined) {
          bidrag[cell.ar] = cell.imported;
        }
      }
      const finansiarer: Record<string, Record<number, number>> = {};
      for (const row of p.finansiering) {
        if (!row.finansiarId) {
          continue;
        }
        for (const cell of row.cells) {
          if (cell.imported === undefined) {
            continue;
          }
          finansiarer[row.finansiarId] ??= {};
          finansiarer[row.finansiarId][cell.ar] = cell.imported;
        }
      }
      return { id: p.part.id, kostnader, bidrag, finansiarer };
    });
  }

  /** Overwrites only the cells present in the patch; other rows are untouched. */
  static mergeIntoTables(
    tables: CurrentBudgetTables,
    patch: BudgetImportApplyProjektpart,
    startYear: number,
  ): CurrentBudgetTables {
    const kostnader = [...tables.kostnader];
    for (const [rowId, perYear] of Object.entries(patch.kostnader)) {
      for (const [year, value] of Object.entries(perYear)) {
        BudgetImportHelper.upsert(
          kostnader,
          rowId,
          (Number(year) - startYear).toString(),
          value,
        );
      }
    }

    const finansiering = [...tables.finansiering];
    for (const [year, value] of Object.entries(patch.bidrag)) {
      BudgetImportHelper.upsert(
        finansiering,
        Finansieringstyp.VINNOVABIDRAG,
        (Number(year) - startYear).toString(),
        value,
      );
    }
    for (const [finansiarId, perYear] of Object.entries(patch.finansiarer)) {
      for (const [year, value] of Object.entries(perYear)) {
        BudgetImportHelper.upsert(
          finansiering,
          finansiarId,
          (Number(year) - startYear).toString(),
          value,
        );
      }
    }

    return { kostnader, finansiering };
  }

  private static buildRow(
    key: { rowType: number } | { finansiarId: string },
    currentRowId: string,
    label: string,
    years: number[],
    cells: BudgetImportCell[],
    currentTable: TableRowValueList,
    startYear: number,
    unit: BudgetImportUnit,
  ): BudgetImportPreviewRow {
    const matches = (cell: BudgetImportCell) =>
      'rowType' in key
        ? cell.rowType === key.rowType
        : cell.finansiarId === key.finansiarId;

    return {
      id: 'rowType' in key ? key.rowType.toString() : key.finansiarId,
      rowType: 'rowType' in key ? key.rowType : undefined,
      finansiarId: 'finansiarId' in key ? key.finansiarId : undefined,
      label,
      cells: years.map((ar) => {
        const imported = cells.find((c) => matches(c) && c.ar === ar);
        const current = currentTable.find(
          (c) =>
            c.rowId === currentRowId &&
            c.columnId === (ar - startYear).toString(),
        );
        return {
          ar,
          current: current?.value ?? 0,
          imported: imported
            ? BudgetImportHelper.scale(imported.summa, unit)
            : undefined,
          source:
            imported?.sourceCell &&
            `${imported.sourceSheet ?? ''}!${imported.sourceCell}`,
        };
      }),
    };
  }

  private static upsert(
    table: TableRowValueList,
    rowId: string,
    columnId: string,
    value: number,
  ): void {
    const index = table.findIndex(
      (c) => c.rowId === rowId && c.columnId === columnId,
    );
    if (index === -1) {
      table.push({ rowId, columnId, value });
    } else {
      table[index] = { ...table[index], value };
    }
  }
}
